import type { VoxelField } from "@/core/field/VoxelField";

export interface ComponentLabelling {
  /** -1 for empty voxels, otherwise the component index. */
  labels: Int32Array;
  /** Voxel count per component. */
  sizes: number[];
  /** Whether each component reaches the mounting plane. */
  touchesBase: boolean[];
}

/**
 * Label the solid voxels into face-connected pieces.
 *
 * This is what lets a cut behave like a saw: once the kerf has separated the
 * foam, anything that is no longer joined to the flat rear face is an offcut
 * and falls away. Six-connectivity is deliberate — pieces meeting only at an
 * edge or a corner are not physically joined, and a slicer would not treat
 * them as one solid either.
 */
export function labelSolidComponents(field: VoxelField): ComponentLabelling {
  const { nx, ny, nz, data } = field;
  const total = nx * ny * nz;
  const labels = new Int32Array(total).fill(-1);
  const sizes: number[] = [];
  const touchesBase: boolean[] = [];

  // Solid material sitting on the mounting plane occupies the first sample
  // layer above it; the layers below are the grid's air padding.
  const baseLayer = field.baseCellJ + 1;
  const stack = new Int32Array(total);

  for (let seed = 0; seed < total; seed++) {
    if ((data[seed] as number) >= 0) continue;
    if (labels[seed] !== -1) continue;

    const component = sizes.length;
    let size = 0;
    let onBase = false;
    let top = 0;
    stack[top++] = seed;
    labels[seed] = component;

    while (top > 0) {
      const index = stack[--top] as number;
      size++;

      const i = index % nx;
      const rest = (index - i) / nx;
      const j = rest % ny;
      const k = (rest - j) / ny;
      if (j <= baseLayer) onBase = true;

      if (i > 0) push(index - 1);
      if (i < nx - 1) push(index + 1);
      if (j > 0) push(index - nx);
      if (j < ny - 1) push(index + nx);
      if (k > 0) push(index - nx * ny);
      if (k < nz - 1) push(index + nx * ny);
    }

    sizes.push(size);
    touchesBase.push(onBase);

    function push(neighbour: number): void {
      if (labels[neighbour] !== -1) return;
      if ((data[neighbour] as number) >= 0) return;
      labels[neighbour] = component;
      stack[top++] = neighbour;
    }
  }

  return { labels, sizes, touchesBase };
}

/**
 * Which piece survives a cut: the biggest one still joined to the mounting
 * plane. Keeping exactly one guarantees the export stays a single solid.
 */
export function chooseSurvivingComponent(labelling: ComponentLabelling): number {
  let best = -1;
  let bestSize = 0;
  for (let c = 0; c < labelling.sizes.length; c++) {
    if (!labelling.touchesBase[c]) continue;
    const size = labelling.sizes[c] as number;
    if (size > bestSize) {
      bestSize = size;
      best = c;
    }
  }
  if (best !== -1) return best;

  // Nothing is on the plane any more — fall back to the largest piece so the
  // user is never left with an empty workspace.
  for (let c = 0; c < labelling.sizes.length; c++) {
    const size = labelling.sizes[c] as number;
    if (size > bestSize) {
      bestSize = size;
      best = c;
    }
  }
  return best;
}
